'use client';

import { useState, useMemo, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Typography, Select, MenuItem, FormControl } from '@mui/material';
import './SessionLineChart.css';
import { axiosInstance } from '../../../../../utils/tokenUtils';

const timeOptions = [
  { value: 'today', label: 'Today' },
  { value: '1_week', label: '1 Week' },
  { value: '2_weeks', label: '2 Weeks' },
  { value: '1_month', label: '1 Month' },
  { value: '3_months', label: '3 Months' },
  { value: '6_months', label: '6 Months' },
  { value: '1_year', label: '1 Year' },
];

const shimmerStyle = {
  width: '100%',
  height: 300,
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s infinite',
  borderRadius: 8,
};

const SessionLineChart = ({ title = 'Session Analytics' }) => {
  const [timeFilter, setTimeFilter] = useState('1_year');
  const [loading, setLoading] = useState(true);
  const [apiData, setApiData] = useState(null);
  const [error, setError] = useState(null);

  // Fetch data when component mounts or timeFilter changes
  useEffect(() => {
    let isMounted = true;

    const fetchDataAsync = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axiosInstance.get(
          `/admin/dashboard/session-growth-chart?period=${timeFilter}`
        );
        if (isMounted) {
          setApiData(response.data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to fetch data');
          console.error('Error fetching session data:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDataAsync();

    return () => {
      isMounted = false;
    };
  }, [timeFilter]);

  // Handle filter change
  const handleFilterChange = event => {
    setTimeFilter(event.target.value);
  };

  // Transform API data to chart format
  const chartData = useMemo(() => {
    // No data: show an empty state, never invented figures (this used to
    // fall back to hard-coded mock numbers rising to 3,500 sessions a month).
    if (!apiData || !apiData.data_points) return [];

    return apiData.data_points.map(point => ({
      name: point.period,
      Academic: point.academic_sessions || 0,
      Creative: point.creative_sessions || 0,
      total: point.total_sessions || 0,
    }));
  }, [apiData]);

  // Nothing to plot: no points, or no sessions at all in the period
  const hasSessions = chartData.some(point => point.Academic > 0 || point.Creative > 0);

  const maxValue = useMemo(() => {
    if (!chartData.length) return 100;
    const allValues = [
      ...chartData.map(item => item.Academic),
      ...chartData.map(item => item.Creative),
      ...chartData.map(item => item.total),
    ];
    const max = Math.max(...allValues, 0);

    // If all values are 0, set a reasonable default
    if (max === 0) return 10;

    // Calculate appropriate max value based on the data range
    if (max <= 10) return 10;
    if (max <= 50) return Math.ceil(max * 1.5);
    if (max <= 100) return Math.ceil(max * 1.3);
    if (max <= 500) return Math.ceil(max * 1.2);
    if (max <= 1000) return Math.ceil(max * 1.2);
    if (max <= 5000) return Math.ceil(max * 1.2);
    if (max <= 10000) return Math.ceil(max * 1.2);
    return Math.ceil(max * 1.2);
  }, [chartData]);

  const getYAxisTicks = max => {
    if (max <= 10) return [0, 2, 4, 6, 8, 10];
    if (max <= 20) return [0, 5, 10, 15, 20];
    if (max <= 50) return [0, 10, 20, 30, 40, 50];
    if (max <= 100) return [0, 20, 40, 60, 80, 100];
    if (max <= 200) return [0, 50, 100, 150, 200];
    if (max <= 500) return [0, 100, 200, 300, 400, 500];
    if (max <= 1000) return [0, 200, 400, 600, 800, 1000];
    if (max <= 2000) return [0, 500, 1000, 1500, 2000];
    if (max <= 5000) return [0, 1000, 2000, 3000, 4000, 5000];
    if (max <= 10000) return [0, 2000, 4000, 6000, 8000, 10000];
    return [0, 5000, 10000, 15000, 20000];
  };
  const yAxisTicks = getYAxisTicks(maxValue);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="session-custom-tooltip">
          <p className="tooltip-label">{label}</p>
          <p className="academic">{`Academic: ${data.Academic}`}</p>
          <p className="creative">{`Creative: ${data.Creative}`}</p>
          <p className="total">{`Total: ${data.total}`}</p>
        </div>
      );
    }
    return null;
  };

  if (error) {
    return (
      <div className="session-analytics-container">
        <div className="session-header">
          <Typography className="session-header-title">{title}</Typography>
        </div>
        <div className="session-chart-container">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '300px',
              color: '#666',
              fontSize: '14px',
            }}
          >
            Error loading chart data: {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="session-analytics-container">
      <div className="session-header">
        <Typography className="session-header-title">{title}</Typography>
        <div className="session-time-filters">
          <FormControl variant="outlined" size="small">
            <Select
              value={timeFilter}
              onChange={handleFilterChange}
              className="time-filter-dropdown"
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      fontSize: '14px',
                      fontFamily: 'Poppins',
                    },
                  },
                },
              }}
            >
              {timeOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      <div className="session-chart-container">
        {loading ? (
          <div style={shimmerStyle} />
        ) : !hasSessions ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '300px',
              color: '#666',
              fontSize: '14px',
            }}
          >
            No session data for this period
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData} margin={{ top: 40, right: 30, left: 20, bottom: 10 }}>
              <defs>
                <linearGradient id="colorAcademic" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0.1} />
                </linearGradient>
                <linearGradient id="colorCreative" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#666', fontSize: 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#666', fontSize: 12 }}
                tickFormatter={value => {
                  if (value >= 1000) return `${value / 1000}K`;
                  return value;
                }}
                domain={[0, maxValue]}
                ticks={yAxisTicks}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="Academic"
                stroke="#2563eb"
                strokeWidth={2}
                fill="url(#colorAcademic)"
                fillOpacity={1}
              />
              <Area
                type="monotone"
                dataKey="Creative"
                stroke="transparent"
                fill="url(#colorCreative)"
                fillOpacity={1}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}

        <div className="session-legend">
          <div className="session-legend-item">
            <div className="session-legend-color academic"></div>
            <span>Academic</span>
          </div>
          <div className="session-legend-item">
            <div className="session-legend-color creative"></div>
            <span>Creative</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionLineChart;
